import React, { useState, useEffect, useContext } from "react";
import {
  Segment,
  Label,
  Input,
  Icon,
  Popup,
  Button,
  Confirm,
  TextArea,
} from "semantic-ui-react";
import { SlidesContextEdit } from "../../../../../../../contexts/MediaContext";
import "../TemplateManager.scss";

export const Smu08 = React.memo((props) => {
  const { index, slideId, onRemoveSlideByIndex } = props;
  const { slidesActiveSessionEdit } = useContext(SlidesContextEdit);
  const [showConfirm, setShowConfirm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [videoId, setVideoId] = useState("");

  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  /**
   * Synchronizes component state with the content of the currently selected slide in Smu08.js.
   *
   * This `useEffect` hook is executed whenever the `slideId` changes. It searches the `slidesActiveSessionEdit`
   * array for a slide that matches the `slideId`. If such a slide is found and it contains content, the hook
   * updates the component's state with the slide's title, description, and video ID.
   *
   * Specifically, it performs the following actions:
   * - Sets the `title` state with the slide's title.
   * - Sets the `description` state with the slide's description.
   * - Sets the `videoId` state with the slide's video ID.
   *
   * This ensures that the component accurately represents the content of the current slide, allowing for
   * effective editing and previewing within an active session.
   *
   * @dependency slideId - The ID of the current slide, changes to which trigger the hook.
   */
  useEffect(() => {
    const slide = slidesActiveSessionEdit.find(
      (slide) => slide._id === slideId
    );
    if (slide && slide.content) {
      setTitle(slide.content.title);
      setDescription(slide.content.description);
      setVideoId(slide.content.video_id);
    }
  }, [slideId]);

  /**
   * Updates the content of a specific slide within the `slidesActiveSessionEdit` array in Smu08.js.
   *
   * This `useEffect` hook is triggered when there are changes to the `title`, `description`, or `videoId` states. It searches for the slide matching the `slideId` within the `slidesActiveSessionEdit` array. If the slide is found (indicated by `slideIndex` not being -1), it updates the slide's content object with the new values for title, description, and video ID.
   *
   * The process is as follows:
   * - Finds the index of the slide with the matching `slideId`.
   * - If the slide is found, updates the slide's `content` object with the new `title`, `description`, and `video_id`.
   *
   * This ensures that any changes made to the slide's title, description, or video ID are immediately reflected within the active session's slide array, keeping the session data up-to-date with the user's edits.
   *
   * @dependency title - The current title value to be updated for the slide.
   * @dependency description - The current description value to be updated for the slide.
   * @dependency videoId - The current video ID to be updated for the slide.
   */
  useEffect(() => {
    const slideIndex = slidesActiveSessionEdit.findIndex(
      (slide) => slide._id === slideId
    );
    if (slideIndex !== -1) {
      slidesActiveSessionEdit[slideIndex].content.title = title;
      slidesActiveSessionEdit[slideIndex].content.description = description;
      slidesActiveSessionEdit[slideIndex].content.video_id = videoId;
    }
  }, [title, description, videoId]);

  /**
   * Event handlers for managing slide content updates in Smu08.js.
   *
   * `handleTitleChange`:
   * - Triggered when the title input field changes.
   * - Updates the `title` state, removing leading whitespace from the input value.
   *
   * `handleDescriptionChange`:
   * - Triggered when the description input field changes.
   * - Updates the `description` state, removing leading whitespace from the input value.
   *
   * `handleVideoIdChange`:
   * - Triggered when the video ID input field changes.
   * - Updates the `videoId` state, removing leading whitespace from the input value.
   *
   * These handlers ensure that the input fields for title, description, and video ID are processed correctly,
   * removing any leading whitespace to maintain clean data entry. This is crucial for maintaining the integrity
   * of the data as it directly affects the content displayed to the user.
   */
  const handleTitleChange = (event) => {
    setTitle(event.target.value.trimStart());
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value.trimStart());
  };

  const handleVideoIdChange = (event) => {
    setVideoId(event.target.value.trimStart());
  };

  return (
    <>
      <Segment raised className="media-slide">
        <div className="media-slide__header">
          <div className="media-slide__header">
            <Label attached="top left">
              Hoja {index + 1} de {slidesActiveSessionEdit.length}
            </Label>
          </div>
          <div className="media-slide__button">
            <Popup
              content="Eliminar hoja"
              trigger={
                <Button
                  compact
                  color="grey"
                  icon="trash alternate outline"
                  onClick={onOpenCloseConfirm}
                />
              }
            />
          </div>
        </div>
        <div className="media-slide__time">
          <div className="media-slide__label-div">
            <Icon name="bookmark" />
            <h5 className="media-slide__label">Título:</h5>
          </div>
          <Input
            fluid
            className="media-slide__input"
            name="title"
            value={title}
            onChange={handleTitleChange}
          />
        </div>
        <div className="row">
          <div className="media-slide__label-div">
            <Icon name="content" />
            <h5 className="media-slide__label">Descripción:</h5>
          </div>
          <TextArea
            className="media-slide__textarea"
            rows={5}
            name="description"
            value={description}
            onChange={handleDescriptionChange}
          />
        </div>
        <div className="row">
          <div className="media-slide__label-div">
            <Icon name="youtube" />
            <h5 className="media-slide__label">ID del video:</h5>
          </div>
          <Input
            fluid
            className="media-slide__input"
            name="videoId"
            value={videoId}
            onChange={handleVideoIdChange}
          />
        </div>
      </Segment>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={() => onRemoveSlideByIndex(index)}
        content={`¿Deseas eliminar esta hoja?`}
        size="mini"
      />
    </>
  );
});
